import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  Zap,
  Gauge,
  Palette,
  Layers,
  Code2,
  Search,
} from "lucide-react";

const FEATURES = [
  {
    span: "md:col-span-2 md:row-span-2",
    title: "14-day delivery, every time.",
    body: "One sprint. One producer, one engineer. From brief to launch in under two weeks — or your deposit back.",
    tag: "Sprint model",
    icon: Zap,
    visual: "clock",
  },
  {
    span: "md:col-span-1",
    title: "Zero-JS by default.",
    body: "Static HTML + a surgical sprinkle of JS. Under 24 kb to interactive.",
    tag: "Performance",
    icon: Gauge,
    visual: "gauge",
  },
  {
    span: "md:col-span-1",
    title: "Original art direction.",
    body: "No template marketplaces. No AI boilerplate. Each brief gets its own typographic voice.",
    tag: "Design",
    icon: Palette,
    visual: "palette",
  },
  {
    span: "md:col-span-1",
    title: "Depth through light.",
    body: "Layered glass, grain and motion — a scene, not a landing page.",
    tag: "Craft",
    icon: Layers,
    visual: "layers",
  },
  {
    span: "md:col-span-1",
    title: "Clean, maintainable code.",
    body: "Semantic HTML, tokenised CSS, a README your next dev will thank you for.",
    tag: "Engineering",
    icon: Code2,
    visual: "code",
  },
  {
    span: "md:col-span-2",
    title: "Built to be found.",
    body: "Server-rendered HTML, OpenGraph, structured data, sitemaps — SEO is a byproduct of doing it right.",
    tag: "SEO",
    icon: Search,
    visual: "search",
  },
];

const Visual = ({ kind }) => {
  if (kind === "clock") {
    return (
      <div className="relative w-full h-full flex items-center justify-center">
        <div className="absolute inset-6 rounded-full border border-white/5" />
        <div className="absolute inset-14 rounded-full border border-white/10" />
        <div className="absolute inset-20 rounded-full border border-accent/30 glow-sm" />
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((i) => (
          <div
            key={i}
            className="absolute w-px h-2 bg-white/20"
            style={{
              top: "50%",
              left: "50%",
              transform: `translate(-50%, -50%) rotate(${i * 30}deg) translateY(-72px)`,
            }}
          />
        ))}
        <motion.div
          className="absolute w-px h-16 bg-accent origin-bottom"
          style={{ top: "calc(50% - 64px)", left: "50%" }}
          animate={{ rotate: 360 }}
          transition={{ duration: 14, repeat: Infinity, ease: "linear" }}
        />
        <div className="relative font-mono text-[11px] text-accent tracking-[0.25em] uppercase">
          14D
        </div>
      </div>
    );
  }
  if (kind === "gauge") {
    return (
      <div className="relative w-full h-full flex items-end justify-center gap-1.5 px-6 pb-6">
        {[18, 32, 46, 58, 72, 88, 76, 94, 82, 100].map((h, i) => (
          <motion.div
            key={i}
            initial={{ height: 0 }}
            whileInView={{ height: `${h}%` }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
            className="w-2 rounded-t"
            style={{
              background:
                i === 9 ? "#FF9E00" : i >= 7 ? "rgba(255,158,0,0.5)" : "rgba(255,255,255,0.12)",
              boxShadow: i === 9 ? "0 0 14px rgba(255,158,0,0.6)" : "none",
            }}
          />
        ))}
      </div>
    );
  }
  if (kind === "palette") {
    return (
      <div className="relative w-full h-full grid grid-cols-6 gap-1 p-6">
        {["#050507", "#121318", "#FF9E00", "#FFC266", "#F4F4F2", "#8A8D98"].map(
          (c, i) => (
            <motion.div
              key={i}
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.6 }}
              className="aspect-[1/3] rounded-sm"
              style={{
                background: c,
                border: "1px solid rgba(255,255,255,0.06)",
                boxShadow: c === "#FF9E00" ? "0 0 20px rgba(255,158,0,0.4)" : "none",
              }}
            />
          )
        )}
      </div>
    );
  }
  if (kind === "layers") {
    return (
      <div className="relative w-full h-full flex items-center justify-center">
        {[0, 1, 2, 3].map((i) => (
          <motion.div
            key={i}
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1, duration: 0.6 }}
            className="absolute w-24 h-16 rounded-md border border-white/10"
            style={{
              background: `rgba(255,158,0,${0.06 + i * 0.05})`,
              transform: `translate(${i * 8}px, ${i * -8}px)`,
              backdropFilter: "blur(4px)",
            }}
          />
        ))}
      </div>
    );
  }
  if (kind === "code") {
    return (
      <div className="relative w-full h-full p-5 font-mono text-[11px] leading-relaxed">
        <div className="text-[#5A5D68]">{"<main>"}</div>
        <div className="pl-3 text-[#8A8D98]">{"<section id=\"hero\">"}</div>
        <div className="pl-6 text-accent">{"<h1>...</h1>"}</div>
        <div className="pl-3 text-[#8A8D98]">{"</section>"}</div>
        <div className="text-[#5A5D68]">{"</main>"}</div>
        <div className="mt-2 pt-2 border-t border-white/5 text-[#5A5D68]">
          <span className="text-accent">●</span> 1.2 kb HTML
        </div>
      </div>
    );
  }
  if (kind === "search") {
    return (
      <div className="relative w-full h-full flex items-center justify-center p-5">
        <div className="w-full max-w-[280px] rounded-lg bg-[#0e0f14] border border-white/10 p-3 font-mono text-[11px]">
          <div className="text-[#5A5D68] mb-1.5 flex items-center gap-1.5">
            <div className="w-1 h-1 rounded-full bg-accent" />
            studio.zcrazy.com
          </div>
          <div className="text-[#F4F4F2] mb-1">Z Crazy — Static sites that look like film</div>
          <div className="text-[#8A8D98] text-[10px] leading-relaxed">
            Hand-coded, zero-JS marketing sites, portfolios and launch pages...
          </div>
          <div className="mt-2 flex gap-1.5 text-[9px]">
            <span className="text-accent">⬢ 100</span>
            <span className="text-[#5A5D68]">PageSpeed</span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

const Card = ({ f, i }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10%" });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    ref.current.style.setProperty("--mx", `${e.clientX - r.left}px`);
    ref.current.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  const Icon = f.icon;

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, delay: 0.05 * i, ease: [0.22, 1, 0.36, 1] }}
      className={`spotlight-card min-h-[280px] p-6 flex flex-col ${f.span}`}
      data-testid="bento-card"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="mono-label flex items-center gap-2">
          <span className="h-px w-5 bg-accent/60" />
          {f.tag}
        </div>
        <div className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-accent">
          <Icon size={15} strokeWidth={1.6} />
        </div>
      </div>

      <div className="flex-1 relative mb-6 rounded-lg overflow-hidden"
        style={{
          background: "linear-gradient(180deg, rgba(255,255,255,0.02), rgba(0,0,0,0.3))",
          minHeight: 160,
          border: "1px solid rgba(255,255,255,0.04)",
        }}
      >
        <Visual kind={f.visual} />
      </div>

      <h3 className="font-display text-[22px] sm:text-[26px] font-semibold tracking-tight leading-[1.1] text-[#F4F4F2] mb-2">
        {f.title}
      </h3>
      <p className="text-[14px] leading-relaxed text-[#8A8D98]">{f.body}</p>
    </motion.div>
  );
};

const FeaturesBento = () => {
  return (
    <section
      id="features"
      data-testid="features-bento-container"
      className="relative py-32 sm:py-44"
    >
      <div className="max-w-[1280px] mx-auto px-6 sm:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <div className="mono-label mb-4">— 02 / Craft</div>
            <h2 className="font-display font-light tracking-tight text-[clamp(2.4rem,5vw,4.8rem)] leading-[0.95] max-w-[18ch]">
              Six disciplines,
              <br />
              <span className="font-editorial italic text-accent">one obsession.</span>
            </h2>
          </div>
          <p className="max-w-[40ch] text-[15px] text-[#8A8D98] leading-relaxed">
            Every project runs through the same six gates. If a decision
            doesn't move all six forward, it doesn't happen.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 auto-rows-[260px]">
          {FEATURES.map((f, i) => (
            <Card key={f.title} f={f} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesBento;
